<template>
  <div class="sound-tag-page">
    <section class="page-hero">
      <div>
        <div class="page-kicker">SOUND LIBRARY ORGANIZER</div>
        <h1>标签与搜索词</h1>
        <p>
          用统一标签整理音效，用搜索词补充用户可能输入的叫法。
          基础分类和 v2 分类仍按原方式工作，不会被这里的操作替换。
        </p>
      </div>
      <el-button icon="el-icon-refresh" :loading="catalogLoading" @click="refreshCurrentPage">
        刷新数据
      </el-button>
    </section>

    <section class="summary-grid" v-loading="catalogLoading">
      <div class="summary-card">
        <div class="summary-icon blue"><i class="el-icon-files"></i></div>
        <div>
          <div class="summary-value">{{ summary.group_count || 0 }}</div>
          <div class="summary-label">标签组</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon purple"><i class="el-icon-collection-tag"></i></div>
        <div>
          <div class="summary-value">{{ summary.tag_count || 0 }}</div>
          <div class="summary-label">标签</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon green"><i class="el-icon-connection"></i></div>
        <div>
          <div class="summary-value">{{ formatNumber(summary.tagged_sound_count) }}</div>
          <div class="summary-label">已整理音效</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon orange"><i class="el-icon-search"></i></div>
        <div>
          <div class="summary-value">{{ formatNumber(summary.search_term_count) }}</div>
          <div class="summary-label">独立搜索词</div>
        </div>
      </div>
    </section>

    <el-alert class="beginner-guide" type="info" :closable="false" show-icon>
      <template slot="title">
        第一次使用？先在“标签库”维护标准标签，再到“音效整理”给一个或多个音效添加标签。
        同义叫法放在“别名”，只属于某个音效的补充词放在“搜索词”。
      </template>
    </el-alert>

    <el-tabs v-model="activeTab" class="workspace-tabs" @tab-click="handleTabClick">
      <el-tab-pane name="catalog">
        <span slot="label"><i class="el-icon-collection-tag"></i> 标签库</span>

        <div class="catalog-layout">
          <el-card class="group-panel" shadow="never">
            <div slot="header" class="panel-header">
              <div>
                <strong>标签组</strong>
                <small>按用途分开管理</small>
              </div>
              <el-tooltip content="新建标签组" placement="top">
                <el-button
                  type="primary"
                  size="mini"
                  circle
                  icon="el-icon-plus"
                  @click="openGroupDialog()"
                ></el-button>
              </el-tooltip>
            </div>

            <div v-if="groups.length" class="group-list">
              <button
                v-for="group in groups"
                :key="group.id"
                type="button"
                class="group-item"
                :class="{ active: selectedGroupId === group.id, disabled: group.status !== 1 }"
                @click="selectGroup(group)"
              >
                <span class="group-color" :style="{ backgroundColor: groupColor(group.id) }"></span>
                <span class="group-main">
                  <span class="group-name">
                    {{ group.name }}
                    <el-tag v-if="group.status !== 1" size="mini" type="info">已停用</el-tag>
                  </span>
                  <span class="group-meta">
                    {{ group.tag_count }} 个标签 · {{ formatNumber(group.relation_count) }} 次关联
                  </span>
                </span>
                <i class="el-icon-arrow-right"></i>
              </button>
            </div>
            <el-empty v-else description="还没有标签组"></el-empty>
          </el-card>

          <el-card class="tag-panel" shadow="never">
            <div slot="header" class="tag-panel-heading">
              <div v-if="selectedGroup">
                <div class="heading-title">
                  <span
                    class="heading-dot"
                    :style="{ backgroundColor: groupColor(selectedGroup.id) }"
                  ></span>
                  {{ selectedGroup.name }}
                </div>
                <div class="heading-description">
                  {{ selectedGroup.description || '这个标签组还没有说明。' }}
                </div>
              </div>
              <div class="heading-actions">
                <el-button
                  v-if="selectedGroup"
                  size="small"
                  icon="el-icon-setting"
                  @click="openGroupDialog(selectedGroup)"
                >
                  设置标签组
                </el-button>
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-plus"
                  :disabled="!selectedGroup"
                  @click="openTagDialog()"
                >
                  新建标签
                </el-button>
              </div>
            </div>

            <div class="tag-toolbar">
              <el-input
                v-model.trim="tagKeyword"
                clearable
                prefix-icon="el-icon-search"
                placeholder="搜索标签名、别名或说明"
              ></el-input>
              <el-select v-model="tagStatus" placeholder="全部状态">
                <el-option label="全部状态" value=""></el-option>
                <el-option label="只看启用" :value="1"></el-option>
                <el-option label="只看停用" :value="0"></el-option>
              </el-select>
              <span class="result-count">当前 {{ visibleTags.length }} 个</span>
            </div>

            <el-table
              v-loading="catalogLoading"
              :data="visibleTags"
              row-key="id"
              :row-class-name="tagRowClassName"
              empty-text="这个标签组还没有标签"
            >
              <el-table-column label="标签" min-width="190">
                <template slot-scope="scope">
                  <div class="tag-name-cell">
                    <div class="tag-title-line">
                      <span class="tag-name">{{ scope.row.name }}</span>
                      <el-tag v-if="scope.row.status !== 1" size="mini" type="info">停用</el-tag>
                    </div>
                    <div v-if="scope.row.parent_name" class="tag-parent">
                      上级：{{ scope.row.parent_name }}
                    </div>
                    <div v-if="scope.row.description" class="tag-description">
                      {{ scope.row.description }}
                    </div>
                    <div v-if="scope.row.semantic_code" class="tag-semantic">
                      <img
                        v-if="imageAssetUrl(scope.row.icon_asset)"
                        :src="imageAssetUrl(scope.row.icon_asset)"
                        alt=""
                      />
                      <i v-else class="el-icon-picture-outline"></i>
                      <code>{{ scope.row.semantic_code }}</code>
                      <span>图标优先级 {{ scope.row.icon_priority }}</span>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="别名" min-width="220">
                <template slot-scope="scope">
                  <div v-if="scope.row.alias_names.length" class="alias-list">
                    <el-tag
                      v-for="alias in scope.row.alias_names.slice(0, 5)"
                      :key="alias"
                      size="mini"
                      effect="plain"
                    >
                      {{ alias }}
                    </el-tag>
                    <el-tooltip
                      v-if="scope.row.alias_names.length > 5"
                      :content="scope.row.alias_names.slice(5).join('、')"
                    >
                      <span class="more-alias">+{{ scope.row.alias_names.length - 5 }}</span>
                    </el-tooltip>
                  </div>
                  <span v-else class="muted-text">暂无别名</span>
                </template>
              </el-table-column>
              <el-table-column label="使用情况" width="130" align="center">
                <template slot-scope="scope">
                  <button type="button" class="relation-link" @click="viewTagSounds(scope.row)">
                    {{ formatNumber(scope.row.relation_count) }} 个音效
                  </button>
                </template>
              </el-table-column>
              <el-table-column label="来源" width="105" align="center">
                <template slot-scope="scope">
                  <el-tag
                    size="mini"
                    :type="scope.row.source === 'manual' ? 'success' : 'info'"
                  >
                    {{ scope.row.source === 'manual' ? '人工创建' : '数据初始化' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="168" align="right">
                <template slot-scope="scope">
                  <el-button type="text" icon="el-icon-edit" @click="openTagDialog(scope.row)">
                    编辑
                  </el-button>
                  <el-button
                    type="text"
                    class="danger-text"
                    icon="el-icon-delete"
                    @click="confirmDeleteTag(scope.row)"
                  >
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane name="sounds">
        <span slot="label"><i class="el-icon-headset"></i> 音效整理</span>

        <el-card class="sound-panel" shadow="never">
          <div class="sound-filter-bar">
            <el-input
              v-model.trim="soundFilters.keyword"
              clearable
              prefix-icon="el-icon-search"
              placeholder="搜音效名、ID、标签、别名或搜索词"
              @keyup.enter.native="searchSounds"
            ></el-input>
            <el-select
              v-model="soundFilters.tag_id"
              clearable
              filterable
              placeholder="按标签筛选"
            >
              <el-option-group v-for="group in groups" :key="group.id" :label="group.name">
                <el-option
                  v-for="tag in group.tags"
                  :key="tag.id"
                  :label="tagOptionLabel(tag)"
                  :value="tag.id"
                ></el-option>
              </el-option-group>
            </el-select>
            <el-select v-model="soundFilters.status" placeholder="全部状态">
              <el-option label="全部状态" value=""></el-option>
              <el-option label="启用" :value="1"></el-option>
              <el-option label="停用" :value="0"></el-option>
            </el-select>
            <el-button type="primary" icon="el-icon-search" @click="searchSounds">查询</el-button>
            <el-button @click="clearSoundFilters">清空</el-button>
          </div>

          <transition name="el-fade-in-linear">
            <div v-if="previewSoundName" class="sound-preview">
              <div class="sound-preview-label">
                <i class="el-icon-headset"></i>
                每次点击“试听”都会从头播放
              </div>
              <div class="sound-preview-player">
                <Player ref="player"></Player>
                <div class="sound-preview-source">
                  <el-tag
                    size="mini"
                    :type="previewSoundSourceType === '外链' ? 'warning' : 'success'"
                  >
                    {{ previewSoundSourceType }}
                  </el-tag>
                  <span>{{ previewSoundAddress }}</span>
                </div>
              </div>
            </div>
          </transition>

          <transition name="batch-bar">
            <div v-if="selectedSounds.length" class="batch-bar">
              <div class="batch-count">
                <i class="el-icon-circle-check"></i>
                已选择 <strong>{{ selectedSounds.length }}</strong> 个音效
              </div>
              <el-select
                v-model="batchTagIds"
                multiple
                filterable
                collapse-tags
                placeholder="选择要批量添加的标签"
              >
                <el-option-group
                  v-for="group in activeGroups"
                  :key="group.id"
                  :label="group.name"
                >
                  <el-option
                    v-for="tag in activeTags(group)"
                    :key="tag.id"
                    :label="tagOptionLabel(tag)"
                    :value="tag.id"
                  ></el-option>
                </el-option-group>
              </el-select>
              <el-button type="primary" :loading="batchSaving" @click="submitBatchTags">
                添加到所选音效
              </el-button>
              <span class="batch-tip">只追加，不会覆盖原有标签</span>
            </div>
          </transition>

          <el-table
            ref="soundTable"
            v-loading="soundLoading"
            :data="soundList"
            row-key="id"
            empty-text="没有找到符合条件的音效"
            @selection-change="handleSoundSelection"
          >
            <el-table-column type="selection" width="48"></el-table-column>
            <el-table-column label="音效" min-width="220">
              <template slot-scope="scope">
                <div class="sound-name">{{ scope.row.name }}</div>
                <div class="sound-meta">
                  ID {{ scope.row.id }}
                  <span v-if="scope.row.category_name">· {{ scope.row.category_name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="当前标签" min-width="360">
              <template slot-scope="scope">
                <div v-if="scope.row.tags.length" class="sound-tag-list">
                  <el-tag
                    v-for="tag in scope.row.tags.slice(0, 6)"
                    :key="tag.id"
                    size="mini"
                    effect="plain"
                  >
                    {{ tag.name }}
                  </el-tag>
                  <el-tooltip
                    v-if="scope.row.tags.length > 6"
                    :content="scope.row.tags.slice(6).map(item => item.name).join('、')"
                  >
                    <span class="more-alias">+{{ scope.row.tags.length - 6 }}</span>
                  </el-tooltip>
                </div>
                <span v-else class="untagged-text">尚未整理</span>
              </template>
            </el-table-column>
            <el-table-column label="搜索词" width="100" align="center">
              <template slot-scope="scope">
                <span class="term-count">{{ scope.row.search_term_count }}</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
                  {{ scope.row.status === 1 ? '启用' : '停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="210" align="right">
              <template slot-scope="scope">
                <el-button
                  type="text"
                  icon="el-icon-video-play"
                  :disabled="!canPreviewSound(scope.row)"
                  @click="playSound(scope.row)"
                >
                  试听
                </el-button>
                <el-button type="primary" plain size="mini" @click="openSoundDrawer(scope.row)">
                  整理
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next, jumper"
              :current-page="soundPage.page_num"
              :page-size="soundPage.page_size"
              :page-sizes="[10, 20, 50, 100]"
              :total="soundPage.total"
              @current-change="handleSoundPageChange"
              @size-change="handleSoundSizeChange"
            ></el-pagination>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      :title="groupForm.id ? '设置标签组' : '新建标签组'"
      :visible.sync="groupDialogVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form ref="groupForm" :model="groupForm" :rules="groupRules" label-position="top">
        <el-form-item label="标签组名称" prop="name">
          <el-input v-model.trim="groupForm.name" maxlength="64" show-word-limit></el-input>
          <div class="field-help">使用普通人能理解的名称，例如“人物角色”“情绪氛围”。</div>
        </el-form-item>
        <el-form-item label="用途说明">
          <el-input
            v-model.trim="groupForm.description"
            type="textarea"
            :rows="3"
            maxlength="255"
            show-word-limit
            placeholder="告诉其他管理员：什么样的标签应该放进这个组"
          ></el-input>
        </el-form-item>
        <div class="two-column-form">
          <el-form-item label="显示顺序">
            <el-input-number v-model="groupForm.sort" :min="0" :max="999999"></el-input-number>
          </el-form-item>
          <el-form-item label="状态">
            <el-switch
              v-model="groupForm.status"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="停用"
            ></el-switch>
          </el-form-item>
        </div>
        <el-form-item label="同一音效能否选择组内多个标签">
          <el-switch
            v-model="groupForm.allow_multiple"
            :active-value="1"
            :inactive-value="0"
            active-text="允许多选"
            inactive-text="只选一个"
          ></el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="groupForm.id"
          class="dialog-delete-button"
          type="text"
          @click="confirmDeleteGroup(groupForm)"
        >
          删除空标签组
        </el-button>
        <span class="dialog-footer-spacer"></span>
        <el-button @click="groupDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="groupSaving" @click="submitGroup">保存</el-button>
      </span>
    </el-dialog>

    <SoundTagDialog
      :visible.sync="tagDialogVisible"
      :groups="groups"
      :tag="tagEditing"
      :default-group-id="tagDialogGroupId"
      @saved="handleTagSaved"
    ></SoundTagDialog>

    <el-drawer
      title="整理音效"
      :visible.sync="soundDrawerVisible"
      size="660px"
      :wrapper-closable="!soundMetadataSaving"
      :close-on-press-escape="!soundMetadataSaving"
    >
      <div class="sound-drawer-body">
        <template v-if="currentSound">
          <div class="drawer-sound-card">
            <div class="drawer-sound-icon"><i class="el-icon-headset"></i></div>
            <div>
              <h2>{{ currentSound.name }}</h2>
              <p>
                ID {{ currentSound.id }}
                <span v-if="currentSound.category_name"> · 基础分类：{{ currentSound.category_name }}</span>
              </p>
            </div>
          </div>

          <SoundTagSearchEditor
            :sound-id="currentSound.id"
            :groups="groups"
            show-legacy
            @changed="handleSoundMetadataChanged"
            @catalog-changed="handleEditorCatalogChanged"
            @saving-change="soundMetadataSaving = $event"
          ></SoundTagSearchEditor>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import apis from '@/api/index'
import Player from '@/components/Player.vue'
import SoundTagDialog from '@/components/sounds/SoundTagDialog.vue'
import SoundTagSearchEditor from '@/components/sounds/SoundTagSearchEditor.vue'
import { buildSoundPreview, canPreviewSound } from '@/lib/soundAdminUi'

const createEmptyGroupForm = () => ({
  id: 0,
  name: '',
  description: '',
  sort: 0,
  status: 1,
  allow_multiple: 1
})

export default {
  name: 'SoundTagWorkspace',
  components: {
    Player,
    SoundTagDialog,
    SoundTagSearchEditor
  },
  data() {
    return {
      activeTab: 'catalog',
      catalogLoading: false,
      summary: {},
      groups: [],
      selectedGroupId: null,
      tagKeyword: '',
      tagStatus: '',
      groupDialogVisible: false,
      groupSaving: false,
      groupForm: createEmptyGroupForm(),
      groupRules: {
        name: [{ required: true, message: '请输入标签组名称', trigger: 'blur' }]
      },
      tagDialogVisible: false,
      tagEditing: null,
      tagDialogGroupId: null,
      soundLoading: false,
      soundList: [],
      selectedSounds: [],
      batchTagIds: [],
      batchSaving: false,
      previewSoundName: '',
      previewSoundSourceType: '',
      previewSoundAddress: '',
      soundFilters: { keyword: '', tag_id: '', status: '' },
      soundPage: { page_num: 1, page_size: 20, total: 0 },
      soundDrawerVisible: false,
      soundMetadataSaving: false,
      currentSound: null
    }
  },
  computed: {
    selectedGroup() {
      return this.groups.find(group => group.id === this.selectedGroupId) || null
    },
    visibleTags() {
      if (!this.selectedGroup) return []
      const keyword = this.tagKeyword.toLowerCase()
      return this.selectedGroup.tags.filter(tag => {
        if (this.tagStatus !== '' && tag.status !== this.tagStatus) return false
        if (!keyword) return true
        const text = [
          tag.name,
          tag.parent_name || '',
          tag.description || '',
          ...(tag.alias_names || [])
        ].join(' ').toLowerCase()
        return text.includes(keyword)
      })
    },
    activeGroups() {
      return this.groups.filter(group => group.status === 1)
    }
  },
  created() {
    this.loadCatalog()
    this.loadSounds()
  },
  methods: {
    canPreviewSound,
    formatNumber(value) {
      return Number(value || 0).toLocaleString('zh-CN')
    },
    imageAssetUrl(item) {
      // 2026-09-16：标签图片直接使用服务端返回的完整文件地址。
      return item && item.full_path ? String(item.full_path) : ''
    },
    groupColor(groupId) {
      const colors = ['#356df3', '#7c4dff', '#00a884', '#ef7d32', '#e84d73', '#1495b5', '#996515', '#596780', '#aa5bca']
      return colors[(Number(groupId || 1) - 1) % colors.length]
    },
    activeTags(group) {
      return (group.tags || []).filter(tag => tag.status === 1)
    },
    tagOptionLabel(tag) {
      return tag.parent_name ? `${tag.parent_name} › ${tag.name}` : tag.name
    },
    tagRowClassName({ row }) {
      return row.status === 1 ? '' : 'disabled-tag-row'
    },
    handleTabClick(tab) {
      if (tab.name === 'sounds' && !this.soundList.length) this.loadSounds()
    },
    refreshCurrentPage() {
      this.loadCatalog()
      if (this.activeTab === 'sounds') this.loadSounds()
    },
    async loadCatalog() {
      this.catalogLoading = true
      try {
        const response = await apis.tag.catalog()
        if (response.data.code !== 1) {
          this.$message.error(response.data.msg || '标签目录加载失败')
          return
        }
        const data = response.data.data || {}
        this.summary = data.summary || {}
        this.groups = data.groups || []
        if (!this.groups.some(group => group.id === this.selectedGroupId)) {
          this.selectedGroupId = this.groups.length ? this.groups[0].id : null
        }
      } catch (error) {
        this.$message.error(String(error || '标签目录加载失败'))
      } finally {
        this.catalogLoading = false
      }
    },
    selectGroup(group) {
      this.selectedGroupId = group.id
      this.tagKeyword = ''
      this.tagStatus = ''
    },
    openGroupDialog(group = null) {
      this.groupForm = group
        ? {
            id: group.id,
            name: group.name,
            description: group.description || '',
            sort: Number(group.sort || 0),
            status: Number(group.status),
            allow_multiple: Number(group.allow_multiple)
          }
        : createEmptyGroupForm()
      this.groupDialogVisible = true
      this.$nextTick(() => this.$refs.groupForm && this.$refs.groupForm.clearValidate())
    },
    submitGroup() {
      this.$refs.groupForm.validate(async valid => {
        if (!valid) return
        this.groupSaving = true
        try {
          const response = await apis.tag.saveGroup(this.groupForm)
          if (response.data.code !== 1) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
          this.selectedGroupId = Number(response.data.data.id)
          this.$message.success('标签组已保存')
          this.groupDialogVisible = false
          await this.loadCatalog()
        } catch (error) {
          this.$message.error(String(error || '保存失败'))
        } finally {
          this.groupSaving = false
        }
      })
    },
    async confirmDeleteGroup(group) {
      try {
        await this.$confirm(
          `只允许删除没有任何标签的空标签组。确定删除“${group.name}”吗？`,
          '删除标签组',
          { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
        )
        const response = await apis.tag.deleteGroup({ id: group.id })
        if (response.data.code !== 1) {
          this.$message.error(response.data.msg || '删除失败')
          return
        }
        this.groupDialogVisible = false
        this.$message.success('标签组已删除')
        await this.loadCatalog()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(String(error || '删除失败'))
      }
    },
    openTagDialog(tag = null) {
      this.tagEditing = tag
      this.tagDialogGroupId = tag
        ? Number(tag.group_id)
        : (this.selectedGroup ? Number(this.selectedGroup.id) : null)
      this.tagDialogVisible = true
    },
    async handleTagSaved(tag) {
      this.selectedGroupId = Number(tag.group_id)
      await this.loadCatalog()
    },
    handleEditorCatalogChanged(groups) {
      this.groups = groups
      const summary = { ...this.summary }
      summary.tag_count = groups.reduce(
        (total, group) => total + (group.tags || []).length,
        0
      )
      this.summary = summary
    },
    async confirmDeleteTag(tag) {
      const message = tag.relation_count > 0
        ? `“${tag.name}”已经关联 ${tag.relation_count} 个音效，系统会阻止误删。建议编辑标签并改为“停用”。`
        : `确定删除没有音效关联的标签“${tag.name}”吗？`
      try {
        await this.$confirm(message, tag.relation_count > 0 ? '建议停用标签' : '删除标签', {
          confirmButtonText: tag.relation_count > 0 ? '仍然检查删除' : '确定删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const response = await apis.tag.deleteTag({ id: tag.id })
        if (response.data.code !== 1) {
          this.$message.warning(response.data.msg || '当前标签不能删除')
          return
        }
        this.$message.success('标签已删除')
        await this.loadCatalog()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(String(error || '删除失败'))
      }
    },
    viewTagSounds(tag) {
      this.activeTab = 'sounds'
      this.soundFilters.tag_id = tag.id
      this.soundFilters.keyword = ''
      this.soundPage.page_num = 1
      this.loadSounds()
    },
    searchSounds() {
      this.soundPage.page_num = 1
      this.loadSounds()
    },
    clearSoundFilters() {
      this.soundFilters = { keyword: '', tag_id: '', status: '' }
      this.soundPage.page_num = 1
      this.loadSounds()
    },
    playSound(sound) {
      const preview = buildSoundPreview(sound)
      if (!preview) {
        this.$message.warning('该音效没有可用的播放地址')
        return
      }

      this.previewSoundName = sound.name
      this.previewSoundSourceType = preview.sourceType
      this.previewSoundAddress = preview.maskedAddress
      this.$nextTick(() => {
        this.$refs.player.play({
          src: preview.url,
          name: sound.name
        })
      })
    },
    async loadSounds() {
      this.soundLoading = true
      try {
        const response = await apis.tag.sounds({
          ...this.soundFilters,
          page_num: this.soundPage.page_num,
          page_size: this.soundPage.page_size
        })
        if (response.data.code !== 1) {
          this.$message.error(response.data.msg || '音效列表加载失败')
          return
        }
        const data = response.data.data || {}
        this.soundList = data.list || []
        this.soundPage.total = Number(data.count || 0)
        this.selectedSounds = []
        this.batchTagIds = []
      } catch (error) {
        this.$message.error(String(error || '音效列表加载失败'))
      } finally {
        this.soundLoading = false
      }
    },
    handleSoundSelection(rows) {
      this.selectedSounds = rows || []
    },
    handleSoundPageChange(page) {
      this.soundPage.page_num = page
      this.loadSounds()
    },
    handleSoundSizeChange(size) {
      this.soundPage.page_size = size
      this.soundPage.page_num = 1
      this.loadSounds()
    },
    async submitBatchTags() {
      if (!this.batchTagIds.length) {
        this.$message.warning('请先选择要添加的标签')
        return
      }
      this.batchSaving = true
      try {
        const response = await apis.tag.batchAddTags({
          sound_ids: this.selectedSounds.map(sound => sound.id),
          tag_ids: this.batchTagIds
        })
        if (response.data.code !== 1) {
          this.$message.error(response.data.msg || '批量添加失败')
          return
        }
        this.$message.success(response.data.msg || '批量添加成功')
        await Promise.all([this.loadCatalog(), this.loadSounds()])
      } catch (error) {
        this.$message.error(String(error || '批量添加失败'))
      } finally {
        this.batchSaving = false
      }
    },
    openSoundDrawer(sound) {
      this.soundMetadataSaving = false
      this.soundDrawerVisible = true
      this.currentSound = { ...sound }
    },
    handleSoundMetadataChanged() {
      Promise.all([this.loadCatalog(), this.loadSounds()])
    }
  }
}
</script>

<style lang="scss" scoped>
.sound-tag-page {
  min-height: 100%;
  padding: 22px;
  box-sizing: border-box;
  background: #f4f7fb;
  color: #1f2d3d;
}

.page-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 30px;
  border: 1px solid #dfe8f5;
  border-radius: 14px;
  background:
    radial-gradient(circle at 85% 10%, rgba(53, 109, 243, 0.13), transparent 36%),
    linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 8px 30px rgba(37, 65, 112, 0.06);

  h1 {
    margin: 5px 0 8px;
    font-size: 28px;
    line-height: 1.25;
    color: #17233d;
  }

  p {
    max-width: 760px;
    margin: 0;
    color: #66758d;
    font-size: 14px;
    line-height: 1.8;
  }
}

.page-kicker {
  color: #356df3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.7px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 16px 0;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 88px;
  padding: 14px 18px;
  box-sizing: border-box;
  border: 1px solid #e3eaf3;
  border-radius: 12px;
  background: #fff;
}

.summary-icon {
  display: inline-flex;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 12px;
  font-size: 21px;

  &.blue { color: #356df3; background: #edf3ff; }
  &.purple { color: #7c4dff; background: #f2edff; }
  &.green { color: #00a884; background: #e9f8f4; }
  &.orange { color: #dc752d; background: #fff2e8; }
}

.summary-value {
  color: #17233d;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
}

.summary-label {
  margin-top: 5px;
  color: #7a879b;
  font-size: 13px;
}

.beginner-guide {
  margin-bottom: 15px;
  border-radius: 10px;
}

.workspace-tabs {
  padding: 0 20px 20px;
  border: 1px solid #e1e8f1;
  border-radius: 14px;
  background: #fff;

  ::v-deep .el-tabs__header { margin-bottom: 20px; }
  ::v-deep .el-tabs__item {
    height: 58px;
    line-height: 58px;
    font-size: 15px;
  }
}

.catalog-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
}

.group-panel,
.tag-panel,
.sound-panel {
  border-color: #e4eaf2;
  border-radius: 12px;
}

.group-panel ::v-deep .el-card__body { padding: 10px; }

.panel-header,
.tag-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  strong {
    display: block;
    color: #22304a;
    font-size: 16px;
  }

  small {
    display: block;
    margin-top: 4px;
    color: #8b97a9;
    font-size: 12px;
  }
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.group-item {
  display: flex;
  width: 100%;
  min-height: 66px;
  padding: 11px 12px;
  align-items: center;
  gap: 11px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: 0.18s ease;

  &:hover { background: #f5f8fc; }
  &.active {
    border-color: #ccdafb;
    background: #edf3ff;
    box-shadow: inset 3px 0 #356df3;
  }
  &.disabled { opacity: 0.62; }
  > i { color: #a1adbd; }
}

.group-color,
.heading-dot {
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
}

.group-main { min-width: 0; flex: 1; }
.group-name {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #2b3850;
  font-size: 14px;
  font-weight: 600;
}
.group-meta {
  display: block;
  margin-top: 6px;
  color: #8a96a8;
  font-size: 12px;
}

.heading-title {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #1e2b43;
  font-size: 18px;
  font-weight: 700;
}
.heading-description {
  margin-top: 5px;
  color: #7c899b;
  font-size: 12px;
}
.heading-actions { display: flex; gap: 8px; }

.tag-toolbar,
.sound-filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  .el-input { width: 320px; }
  .el-select { width: 160px; }
}

.sound-filter-bar {
  padding: 16px;
  border-radius: 10px;
  background: #f7f9fc;

  .el-input { width: 360px; }
  .el-select:nth-of-type(1) { width: 240px; }
}

.sound-preview {
  display: flex;
  min-height: 58px;
  margin: -4px 0 16px;
  padding: 12px 16px;
  align-items: center;
  gap: 18px;
  border: 1px solid #d8e4ff;
  border-radius: 10px;
  background: #f4f7ff;

  .sound-preview-label {
    flex: 0 0 auto;
    color: #61708a;
    font-size: 12px;

    i {
      margin-right: 5px;
      color: #356df3;
    }
  }

  .sound-preview-player {
    min-width: 0;
    flex: 1;
  }

  .sound-preview-player ::v-deep > div {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
  }

  ::v-deep p {
    min-width: 120px;
    margin: 0;
    overflow: hidden;
    color: #26344c;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep audio {
    width: 100%;
    max-width: 460px;
    height: 36px;
  }

  .sound-preview-source {
    display: flex;
    margin-top: 7px;
    align-items: center;
    gap: 8px;
    color: #7a8799;
    font-size: 12px;
  }
}

.result-count {
  margin-left: auto;
  color: #8a96a8;
  font-size: 12px;
}
.tag-name-cell { padding: 3px 0; }
.tag-title-line { display: flex; align-items: center; gap: 8px; }
.tag-semantic {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  color: #8a6d1f;
  font-size: 12px;
}
.tag-semantic img,
.tag-semantic > i {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border-radius: 5px;
  object-fit: contain;
  background: #f1f3f6;
}
.tag-semantic > i {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.tag-semantic code {
  padding: 2px 5px;
  border-radius: 4px;
  background: #fff6d9;
  color: #8a6d1f;
}
.tag-name,
.sound-name {
  color: #26344c;
  font-size: 14px;
  font-weight: 600;
}
.tag-parent,
.tag-description,
.sound-meta {
  margin-top: 4px;
  color: #909cad;
  font-size: 12px;
  line-height: 1.5;
}
.alias-list,
.sound-tag-list,
.selected-tag-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.more-alias {
  display: inline-flex;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 12px;
  background: #f0f3f7;
  color: #66758a;
  font-size: 12px;
}
.muted-text { color: #a0aaba; font-size: 12px; }
.relation-link {
  padding: 4px 8px;
  border: 0;
  background: transparent;
  color: #356df3;
  cursor: pointer;
}
.danger-text { color: #e25757 !important; }
::v-deep .disabled-tag-row { opacity: 0.58; background: #fafafa; }

.batch-bar {
  display: flex;
  min-height: 58px;
  margin-bottom: 14px;
  padding: 10px 14px;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  border: 1px solid #cddbf9;
  border-radius: 10px;
  background: #f0f5ff;

  .el-select { width: 330px; }
}
.batch-count {
  min-width: 155px;
  color: #36516f;
  i { margin-right: 5px; color: #356df3; }
}
.batch-tip { color: #73829a; font-size: 12px; }
.batch-bar-enter-active,
.batch-bar-leave-active { transition: all 0.2s ease; }
.batch-bar-enter,
.batch-bar-leave-to { opacity: 0; transform: translateY(-6px); }

.untagged-text {
  display: inline-block;
  padding: 4px 9px;
  border-radius: 12px;
  background: #fff4e7;
  color: #b66b23;
  font-size: 12px;
}
.term-count { color: #4e5f77; font-weight: 600; }
.pagination-wrap { display: flex; justify-content: flex-end; padding-top: 18px; }

.two-column-form,
.three-column-form { display: grid; gap: 16px; }
.two-column-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.three-column-form { grid-template-columns: 1.2fr 1fr 1fr; }
.field-help {
  margin-top: 6px;
  color: #8a96a8;
  font-size: 12px;
  line-height: 1.5;
}
.dialog-footer { display: flex; width: 100%; align-items: center; }
.dialog-footer-spacer { flex: 1; }
.dialog-delete-button { color: #e25757; }

.sound-drawer-body { min-height: 300px; padding: 0 24px 30px; }
.drawer-sound-card {
  display: flex;
  padding: 18px;
  align-items: center;
  gap: 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #edf3ff, #f8fbff);

  h2 { margin: 0; color: #1f2d45; font-size: 18px; }
  p { margin: 6px 0 0; color: #718099; font-size: 12px; }
}
.drawer-sound-icon {
  display: inline-flex;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 12px;
  background: #356df3;
  color: #fff;
  font-size: 21px;
}
.drawer-sound-card + .sound-tag-search-editor { margin-top: 18px; }

@media (max-width: 1200px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .catalog-layout { grid-template-columns: 240px minmax(0, 1fr); }
  .sound-filter-bar { flex-wrap: wrap; }
}

@media (max-width: 900px) {
  .sound-tag-page { padding: 12px; }
  .page-hero,
  .tag-panel-heading,
  .sound-preview,
  .batch-bar { align-items: stretch; flex-direction: column; }
  .catalog-layout { grid-template-columns: 1fr; }
  .group-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .tag-toolbar {
    align-items: stretch;
    flex-direction: column;
    .el-input,
    .el-select { width: 100%; }
  }
  .result-count { margin-left: 0; }
  .two-column-form,
  .three-column-form { grid-template-columns: 1fr; }
}
</style>
